import { useState, memo, useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
	Box,
	Tabs,
	Tab,
	Typography,
	IconButton,
	CardMedia,
} from '@mui/material'
import CommonModal from '../../../atoms/modal/CommonModal'
import ButtonCustom from '../../../atoms/button/ButtonCustom'
import InputCustom from '../../../atoms/input/InputCustom'
import SelectCustom from '../../../atoms/select/SelectCustom'
import TextArea from '../../../atoms/textarea/TextArea'
import ImageUploader from '../../../atoms/image-uploader/ImageUploader'
import { PACKAGE_PRICE_TYPE_OPTIONS } from '../../../../util/constants'
import { formatCommonAmount } from '../../../../util/formatAmount'
import { getAddOns } from '../../../../features/packageSlice'
import PackageStyles from '../package.module.scss'

const TabPanel = ({ children, value, index }) => (
	<div hidden={value !== index}>
		{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
	</div>
)

const PackageModal = memo(
	({
		open,
		onClose,
		isEditing,
		formData,
		onFormDataChange,
		onImageChange,
		onSave,
		packageCreated,
		originalData,
		packageId,
		onAddonCreate,
		onAddonEdit,
		onAddonDelete,
		onAddonAttach,
		isLoading,
	}) => {
		const [tabValue, setTabValue] = useState(0)
		const [isDisabled, setIsDisabled] = useState(false)

		useEffect(() => {
			if (!open) {
				setTabValue(0)
				setIsDisabled(false)
			}
		}, [open])

		const handleTabChange = useCallback(
			(e, newValue) => {
				if (newValue === 1 && !packageCreated && !isEditing) {
					return
				}
				setTabValue(newValue)
			},
			[packageCreated, isEditing]
		)

		const handleImageChange = useCallback(
			(file) => {
				onImageChange(file)
			},
			[onImageChange]
		)

		const handleSave = useCallback(() => {
			setIsDisabled(true)
			setTimeout(() => {
				setIsDisabled(false)
			}, 5000)
			onSave()
		}, [onSave])

		const hasChanges = useMemo(() => {
			if (!originalData) return true

			return (
				formData.name !== originalData.name ||
				formData.description !== originalData.description ||
				formData.priceType !== originalData.priceType ||
				formData.price !== originalData.price ||
				formData.image !== null ||
				formData.imagePreview !== originalData.imagePreview
			)
		}, [formData, originalData])

		const showFooter =
			tabValue === 0 &&
			(isEditing ? hasChanges : !packageCreated || hasChanges)

		return (
			<CommonModal
				open={open}
				handleClose={onClose}
				heading={isEditing ? 'Edit Package' : 'Create Package'}
				footerContent={
					showFooter ? (
						<>
							<ButtonCustom
								variant="outlined"
								size="lg"
								onClick={onClose}
							>
								Cancel
							</ButtonCustom>
							<ButtonCustom
								variant="default"
								size="lg"
								onClick={handleSave}
								disabled={isDisabled || isLoading}
							>
								{isLoading
									? 'Saving...'
									: isEditing
										? 'Save Changes'
										: 'Create Package'}
							</ButtonCustom>
						</>
					) : null
				}
			>
				<Box>
					<Box
						sx={{ borderBottom: 1, borderColor: '#DFE4EB', mb: 2 }}
					>
						<Tabs
							value={tabValue}
							onChange={handleTabChange}
							sx={{
								'& .MuiTabs-indicator': {
									backgroundColor: '#7CA6BD',
								},
								'& .MuiButtonBase-root': {
									fontSize: '14px',
									fontWeight: '500',
									color: '#313131',
									textTransform: 'none',
									'&.Mui-selected': {
										color: '#7CA6BD',
									},
								},
							}}
						>
							<Tab label="Package Details" />
							<Tab
								label="Add-Ons"
								disabled={!packageCreated && !isEditing}
								sx={{
									opacity:
										!packageCreated && !isEditing ? 0.5 : 1,
									cursor:
										!packageCreated && !isEditing
											? 'not-allowed'
											: 'pointer',
								}}
							/>
						</Tabs>
					</Box>

					<TabPanel value={tabValue} index={0}>
						<PackageDetailsTab
							formData={formData}
							onFormDataChange={onFormDataChange}
							onImageChange={handleImageChange}
						/>
					</TabPanel>

					<TabPanel value={tabValue} index={1}>
						<AddOnsTab
							addOnsIds={formData.addOnsIds || []}
							packageId={packageId}
							onAddonCreate={onAddonCreate}
							onAddonEdit={onAddonEdit}
							onAddonDelete={onAddonDelete}
							onAddonAttach={onAddonAttach}
						/>
					</TabPanel>
				</Box>
			</CommonModal>
		)
	}
)

PackageModal.displayName = 'PackageModal'

const PackageDetailsTab = memo(
	({ formData, onFormDataChange, onImageChange }) => {
		return (
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<ImageUploader
					label="Package Thumbnail"
					value={formData.image || formData.imagePreview || null}
					onChange={onImageChange}
					accept="image/*"
					multiple={false}
					width="120px"
					height="120px"
					labelStyle={{
						fontWeight: '400',
						color: '#8692A4',
					}}
				/>

				<InputCustom
					label="Package Name*"
					placeholder="e.g. Premium Wedding Package"
					value={formData.name}
					name="name"
					data={formData}
					setDataFunction={onFormDataChange}
					labelStyle={{
						fontWeight: '400',
						color: '#8692A4',
					}}
				/>
				<Box>
					<Typography
						variant="body2"
						sx={{
							mb: 1,
							fontSize: '14px',
							fontWeight: '400',
							color: '#8692A4',
						}}
					>
						Description*
					</Typography>
					<TextArea
						placeholder="Describe what's included in this package..."
						value={formData.description}
						onChange={(e) =>
							onFormDataChange({
								...formData,
								description: e.target.value,
							})
						}
						sx={{ minHeight: '100px' }}
					/>
				</Box>
				<Box>
					<Typography
						variant="body2"
						sx={{
							mb: 1,
							fontSize: '14px',
							fontWeight: '400',
							color: '#8692A4',
						}}
					>
						Price Type*
					</Typography>
					<SelectCustom
						label="Price Type"
						options={PACKAGE_PRICE_TYPE_OPTIONS}
						defaultValue={formData.priceType}
						setDataFunction={(option) =>
							onFormDataChange({
								...formData,
								priceType: option?.value || '',
							})
						}
					/>
				</Box>
				<InputCustom
					label="Price*"
					placeholder="0.00"
					value={formData.price}
					name="price"
					type="number"
					data={formData}
					setDataFunction={(val) => {
						if (val.price >= 0 && val.price <= 99999)
							onFormDataChange(val)
					}}
					labelStyle={{
						fontWeight: '400',
						color: '#8692A4',
					}}
				/>
			</Box>
		)
	}
)

PackageDetailsTab.displayName = 'PackageDetailsTab'

const AddOnsTab = memo(
	({
		addOnsIds,
		packageId,
		onAddonCreate,
		onAddonEdit,
		onAddonDelete,
		onAddonAttach,
	}) => {
		const [browseAddonsOpen, setBrowseAddonsOpen] = useState(false)
		const dispatch = useDispatch()
		const reduxAddOns = useSelector((state) => state.packages.addOns)

		useEffect(() => {
			if (packageId && browseAddonsOpen) {
				dispatch(getAddOns({ packageId }))
			}
		}, [packageId, browseAddonsOpen, dispatch])

		const excludedAddons = useMemo(() => {
			if (!reduxAddOns?.length) return []
			return reduxAddOns.filter(
				(rest) => !addOnsIds.some((exist) => exist._id === rest._id)
			)
		}, [reduxAddOns, addOnsIds])

		return (
			<Box>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'start',
						gap: 2,
						marginBottom: '16px',
					}}
					className={PackageStyles.packageAddonsHeader}
				>
					<Typography
						variant="body2"
						sx={{ color: '#666666', fontSize: '14px' }}
					>
						Add-ons are optional services that couples can select
						with this package.
					</Typography>
					<Box>
						<ButtonCustom
							variant="outlined"
							size="md"
							onClick={() =>
								setBrowseAddonsOpen(!browseAddonsOpen)
							}
						>
							<img src="/images/folder-icon.svg" alt="browse" />
							Browse
							{browseAddonsOpen ? (
								<img src="/images/arrow-up.svg" alt="close" />
							) : (
								<img src="/images/arrow-down.svg" alt="open" />
							)}
						</ButtonCustom>
					</Box>
					<Box sx={{ flexShrink: 0 }}>
						<ButtonCustom
							variant="outlined"
							size="md"
							onClick={() => onAddonCreate(null, null)}
						>
							<img src="/images/plus-icon.svg" alt="add" />
							Add Add-On
						</ButtonCustom>
					</Box>
				</Box>
				<Box>
					{browseAddonsOpen && (
						<>
							<Box
								sx={{
									bgcolor: '#DFE4EB',
									borderRadius: '12px',
									padding: '12px',
								}}
							>
								<div
									className={
										PackageStyles.packageAddonsLibraryTitle
									}
								>
									Available Add-Ons Library
								</div>
								{excludedAddons?.length > 0 ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'column',
											gap: 2,
											marginTop: '16px',
										}}
									>
										{excludedAddons.map((addon, index) => (
											<Box
												key={addon?._id || index}
												className={
													PackageStyles.packageAddonsItem
												}
											>
												<CardMedia
													component="img"
													sx={{
														width: '72px',
														height: '72px',
														objectFit: 'cover',
														borderRadius: '12px',
													}}
													image={
														(typeof addon.image ===
															'string' &&
															addon.image) ||
														addon.imagePreview ||
														'/images/placeholder.png'
													}
													alt={addon.name}
												/>
												<Box sx={{ flex: 1 }}>
													<Box
														sx={{
															display: 'flex',
															justifyContent:
																'space-between',
															alignItems:
																'flex-start',
															mb: 1,
														}}
													>
														<Box>
															<Box
																className={
																	PackageStyles.packageAddonsItemNameMain
																}
															>
																<Typography
																	variant="h6"
																	className={
																		PackageStyles.packageAddonsItemName
																	}
																	sx={{
																		fontWeight:
																			'bold',
																		fontSize:
																			'16px',
																	}}
																>
																	{addon.name ||
																		addon}
																</Typography>
																<Box
																	className={
																		PackageStyles.packageAddonsItemTags
																	}
																>
																	{addon.type && (
																		<Box
																			className={
																				PackageStyles.packageAddonsItemTagsDefault
																			}
																		>
																			{
																				addon.type
																			}
																		</Box>
																	)}
																</Box>
															</Box>
															{addon.description && (
																<Typography
																	variant="body2"
																	className={
																		PackageStyles.packageAddonsItemDescription
																	}
																>
																	{
																		addon.description
																	}
																</Typography>
															)}
															{addon.price !==
																undefined && (
																<Typography
																	variant="h6"
																	className={
																		PackageStyles.packageAddonsItemPrice
																	}
																>
																	{formatCommonAmount(
																		addon.price
																	)}
																</Typography>
															)}
														</Box>
														<Box>
															<ButtonCustom
																variant="default"
																size="md"
																onClick={() =>
																	onAddonAttach?.(
																		packageId,
																		addon?._id
																	)
																}
															>
																<img
																	src="/images/plus-white-icon.svg"
																	alt="attach"
																/>
																Attach
															</ButtonCustom>
														</Box>
													</Box>
												</Box>
											</Box>
										))}
									</Box>
								) : (
									<Box
										sx={{
											border: '1px solid #E0E0E0',
											borderRadius: '8px',
											padding: '30px',
											textAlign: 'center',
											backgroundColor: '#F9F9F9',
										}}
									>
										<h3 className={PackageStyles.noAddonsTitle}>No more available add-ons.</h3>
										<p className={PackageStyles.noAddonsDescription} onClick={() => onAddonCreate(null, null)}>Create your new add-on for this package.</p>
									</Box>
								)}
							</Box>
						</>
					)}
					{addOnsIds && addOnsIds.length > 0 ? (
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: 2,
								marginTop: '16px',
							}}
						>
							{addOnsIds.map((addon, index) => (
								<Box
									key={addon?._id || index}
									className={PackageStyles.packageAddonsItem}
								>
									<CardMedia
										component="img"
										sx={{
											width: '72px',
											height: '72px',
											objectFit: 'cover',
											borderRadius: '12px',
										}}
										image={
											(typeof addon.image === 'string' &&
												addon.image) ||
											addon.imagePreview ||
											'/images/placeholder.png'
										}
										alt={addon.name}
									/>
									<Box sx={{ flex: 1 }}>
										<Box
											sx={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'flex-start',
												mb: 1,
											}}
										>
											<Box>
												<Box
													className={
														PackageStyles.packageAddonsItemNameMain
													}
												>
													<Typography
														variant="h6"
														className={
															PackageStyles.packageAddonsItemName
														}
														sx={{
															fontWeight: 'bold',
															fontSize: '16px',
														}}
													>
														{addon.name || addon}
													</Typography>
													<Box
														className={
															PackageStyles.packageAddonsItemTags
														}
													>
														{addon.isThisAddOnIsRequired && (
															<Box
																className={
																	PackageStyles.packageAddonsItemRequired
																}
															>
																Required
															</Box>
														)}
														{addon.type && (
															<Box
																className={
																	PackageStyles.packageAddonsItemTagsDefault
																}
															>
																{addon.type}
															</Box>
														)}
													</Box>
												</Box>
												{addon.description && (
													<Typography
														variant="body2"
														className={
															PackageStyles.packageAddonsItemDescription
														}
													>
														{addon.description}
													</Typography>
												)}
												{addon.price !== undefined && (
													<Typography
														variant="h6"
														className={
															PackageStyles.packageAddonsItemPrice
														}
													>
														{formatCommonAmount(
															addon.price
														)}
													</Typography>
												)}
											</Box>
											<Box
												sx={{ display: 'flex', gap: 1 }}
											>
												<IconButton
													size="small"
													onClick={() =>
														onAddonEdit &&
														onAddonEdit(
															addon || index
														)
													}
												>
													<img
														src="/images/edit-icon.svg"
														alt="edit"
														style={{
															width: '16px',
															height: '16px',
														}}
													/>
												</IconButton>
												<IconButton
													size="small"
													onClick={() =>
														onAddonDelete &&
														onAddonDelete(
															addon || index
														)
													}
												>
													<img
														src="/images/trash-red.svg"
														alt="delete"
														style={{
															width: '16px',
															height: '16px',
														}}
													/>
												</IconButton>
											</Box>
										</Box>
									</Box>
								</Box>
							))}
						</Box>
					) : (
						<Box
							sx={{
								border: '1px solid #E0E0E0',
								borderRadius: '8px',
								padding: '30px',
								textAlign: 'center',
								backgroundColor: '#F9F9F9',
							}}
						>
							<h3 className={PackageStyles.noAddonsTitle}>No add-ons yet.</h3>
							<p className={PackageStyles.noAddonsDescription} onClick={() => onAddonCreate(null, null)}>Create your first add-on for this package.</p>
						</Box>
					)}
				</Box>
			</Box>
		)
	}
)

AddOnsTab.displayName = 'AddOnsTab'

export default PackageModal
